import React, {useMemo} from 'react';
import {Box, Stack} from '@mui/material';
import {Body, Headline, Icon, Tooltip} from '@lightricks/react-design-system';
import {AssetError} from '@contentlab/types/projectProps';
import translate from '@/utils/translate';

interface ErrorsTooltipProps {
  errors: AssetError[];
  children?: React.ReactNode | null;
}

function ErrorRow({
  title,
  description,
  startIcon,
  highlightTitle,
}: AssetError & {startIcon?: React.ReactNode; highlightTitle?: boolean}) {
  const textElement = highlightTitle ? (
    <>
      <Headline size="xs">{title}</Headline>{' '}
      <Body size="md">{description}</Body>
    </>
  ) : (
    <Body size="md">
      {title} {description}
    </Body>
  );

  return (
    <Box display="flex" alignItems="center" gap="4px" whiteSpace="pre-wrap">
      {startIcon}
      {textElement}
    </Box>
  );
}

function ErrorsTooltip({
  errors,
  children = (
    <Icon
      size="medium"
      appearance="danger"
      name="Actions-Info-Line"
      style={{width: '20px', height: '20px'}}
    />
  ),
}: ErrorsTooltipProps) {
  const tooltipContent = useMemo(() => {
    if (errors.length === 1) {
      const [error] = errors;
      return <ErrorRow {...error} highlightTitle />;
    }
    return (
      <Stack gap="8px">
        <Body size="lg" sx={{fontWeight: 700}}>
          {translate('Error Details:')}
        </Body>
        {errors.map((error) => (
          <ErrorRow
            key={error.title}
            {...error}
            startIcon={
              <Icon
                size="medium"
                appearance="neutral"
                name="Actions-Info-Line"
                style={{width: '20px', height: '20px'}}
              />
            }
          />
        ))}
      </Stack>
    );
  }, [errors]);

  return (
    <Tooltip title={tooltipContent} placement="top" arrow>
      <Stack>{children}</Stack>
    </Tooltip>
  );
}

export default ErrorsTooltip;
